/**
 * V2VariableEditDialog
 *
 * Full edit dialog for FunnelVariable: name, description, and default value.
 * Default values are content-only (@variable text substitution) — NOT for conditions.
 *
 * Type-specific default value editors:
 *   string  → TextField per locale ({ [locale]: string })
 *   number  → TextField type="number"
 *   boolean → Switch
 *   date    → TextField type="date"
 *   enum    → RadioGroup on options (one isDefault)
 *   enum[]  → Checkboxes on options (multiple isDefault)
 *
 * @module v2-variable-utils
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Alert,
  IconButton,
  Button,
  Divider,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { SDialog } from '@modules/ui-components/dialogs/SDialog';
import type { FunnelVariable, VariableOption } from '@segmently/schema-models/v2';
import { V2_VARIABLES } from '@test-kit/selectors/variable-privacy';
import { AnalyticsPrivacyDecisionField } from './AnalyticsPrivacyDecisionField';

// ============================================================================
// TYPES
// ============================================================================

export interface V2VariableEditDialogProps {
  open: boolean;
  variable: FunnelVariable;
  onSave: (variable: FunnelVariable) => Promise<void>;
  onClose: () => void;
  disabled?: boolean;
  /** Available project locales for string default value dropdown. Falls back to ['en-US']. */
  locales?: string[];
}

// ============================================================================
// HELPERS
// ============================================================================

/** Deep clone a FunnelVariable for local editing */
function cloneVariable(v: FunnelVariable): FunnelVariable {
  return JSON.parse(JSON.stringify(v)) as FunnelVariable;
}

// ============================================================================
// SUB-COMPONENTS: Default Value Editors
// ============================================================================

/** String default: localized { [locale]: string } with locale dropdown */
const StringDefaultEditor: React.FC<{
  defaultValue: Record<string, string> | undefined;
  onChange: (dv: Record<string, string> | undefined) => void;
  locales: string[];
  disabled?: boolean;
}> = ({ defaultValue, onChange, locales, disabled }) => {
  const localeEntries = useMemo(() => {
    if (!defaultValue || typeof defaultValue !== 'object') return [{ locale: locales[0] || 'en-US', text: '' }];
    const entries = Object.entries(defaultValue).map(([locale, text]) => ({ locale, text }));
    return entries.length > 0 ? entries : [{ locale: locales[0] || 'en-US', text: '' }];
  }, [defaultValue, locales]);

  // Locales not yet used in entries — available for "Add locale"
  const availableLocales = useMemo(() => {
    const used = new Set(localeEntries.map(e => e.locale));
    return locales.filter(l => !used.has(l));
  }, [locales, localeEntries]);

  const handleTextChange = useCallback((index: number, text: string) => {
    const updated = [...localeEntries];
    updated[index] = { ...updated[index], text };
    const result: Record<string, string> = {};
    for (const e of updated) {
      if (e.text) result[e.locale] = e.text;
    }
    onChange(Object.keys(result).length > 0 ? result : undefined);
  }, [localeEntries, onChange]);

  const handleLocaleChange = useCallback((index: number, locale: string) => {
    const updated = [...localeEntries];
    updated[index] = { ...updated[index], locale };
    const result: Record<string, string> = {};
    for (const e of updated) {
      result[e.locale] = e.text;
    }
    onChange(Object.keys(result).length > 0 ? result : undefined);
  }, [localeEntries, onChange]);

  const handleAddLocale = useCallback(() => {
    if (availableLocales.length === 0) return;
    const result: Record<string, string> = {};
    for (const e of localeEntries) {
      result[e.locale] = e.text;
    }
    result[availableLocales[0]] = '';
    onChange(result);
  }, [localeEntries, availableLocales, onChange]);

  const handleRemoveLocale = useCallback((index: number) => {
    if (localeEntries.length <= 1) {
      onChange(undefined);
      return;
    }
    const updated = localeEntries.filter((_, i) => i !== index);
    const result: Record<string, string> = {};
    for (const e of updated) {
      if (e.text) result[e.locale] = e.text;
    }
    onChange(Object.keys(result).length > 0 ? result : undefined);
  }, [localeEntries, onChange]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {localeEntries.map((entry, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={entry.locale}
              onChange={(e) => handleLocaleChange(i, e.target.value)}
              disabled={disabled}
            >
              {/* Current locale always shown */}
              <MenuItem value={entry.locale}>{entry.locale}</MenuItem>
              {/* Other available locales */}
              {locales
                .filter(l => l !== entry.locale && !localeEntries.some((e, idx) => idx !== i && e.locale === l))
                .map(l => (
                  <MenuItem key={l} value={l}>{l}</MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            value={entry.text}
            onChange={(e) => handleTextChange(i, e.target.value)}
            size="small"
            fullWidth
            disabled={disabled}
            placeholder="Default text for this locale"
          />
          <IconButton
            size="small"
            onClick={() => handleRemoveLocale(i)}
            disabled={disabled || localeEntries.length <= 1}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      {availableLocales.length > 0 && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddLocale}
          disabled={disabled}
          sx={{ alignSelf: 'flex-start' }}
        >
          Add locale
        </Button>
      )}
    </Box>
  );
};

/** Number default */
const NumberDefaultEditor: React.FC<{
  defaultValue: number | undefined;
  onChange: (dv: number | undefined) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
}> = ({ defaultValue, onChange, min, max, disabled }) => (
  <TextField
    type="number"
    size="small"
    fullWidth
    value={defaultValue ?? ''}
    onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
    disabled={disabled}
    placeholder="Default number value"
    inputProps={{ min, max }}
  />
);

/** Boolean default */
const BooleanDefaultEditor: React.FC<{
  defaultValue: boolean | undefined;
  onChange: (dv: boolean | undefined) => void;
  disabled?: boolean;
}> = ({ defaultValue, onChange, disabled }) => (
  <FormControlLabel
    control={
      <Switch
        checked={defaultValue ?? false}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
    }
    label={defaultValue ? 'true' : 'false'}
  />
);

/** Date default */
const DateDefaultEditor: React.FC<{
  defaultValue: string | undefined;
  onChange: (dv: string | undefined) => void;
  disabled?: boolean;
}> = ({ defaultValue, onChange, disabled }) => (
  <TextField
    type="date"
    size="small"
    fullWidth
    value={defaultValue ?? ''}
    onChange={(e) => onChange(e.target.value || undefined)}
    disabled={disabled}
    InputLabelProps={{ shrink: true }}
  />
);

/** Enum default: RadioGroup — one option can be isDefault */
const EnumDefaultEditor: React.FC<{
  options: VariableOption[];
  onChange: (options: VariableOption[]) => void;
  disabled?: boolean;
}> = ({ options, onChange, disabled }) => {
  const selectedId = useMemo(
    () => options.find(o => (o as unknown as Record<string, unknown>).isDefault)?.id ?? '',
    [options],
  );

  const handleChange = useCallback((optionId: string) => {
    onChange(options.map(o => {
      const copy = { ...o };
      if (o.id === optionId) {
        (copy as unknown as Record<string, unknown>).isDefault = true;
      } else {
        delete (copy as unknown as Record<string, unknown>).isDefault;
      }
      return copy;
    }));
  }, [options, onChange]);

  const handleClear = useCallback(() => {
    onChange(options.map(o => {
      const copy = { ...o };
      delete (copy as unknown as Record<string, unknown>).isDefault;
      return copy;
    }));
  }, [options, onChange]);

  return (
    <RadioGroup value={selectedId} onChange={(e) => handleChange(e.target.value)}>
      <FormControlLabel value="" control={<Radio size="small" />} label="None" onClick={handleClear} disabled={disabled} />
      {options.map(o => (
        <FormControlLabel
          key={o.id}
          value={o.id}
          control={<Radio size="small" />}
          label={o.label}
          disabled={disabled}
        />
      ))}
    </RadioGroup>
  );
};

/** Enum[] default: Checkboxes — multiple options can be isDefault */
const EnumMultiDefaultEditor: React.FC<{
  options: VariableOption[];
  onChange: (options: VariableOption[]) => void;
  disabled?: boolean;
}> = ({ options, onChange, disabled }) => {
  const handleToggle = useCallback((optionId: string) => {
    onChange(options.map(o => {
      if (o.id !== optionId) return o;
      const copy = { ...o };
      if ((copy as unknown as Record<string, unknown>).isDefault) {
        delete (copy as unknown as Record<string, unknown>).isDefault;
      } else {
        (copy as unknown as Record<string, unknown>).isDefault = true;
      }
      return copy;
    }));
  }, [options, onChange]);

  return (
    <Box>
      {options.map(o => (
        <FormControlLabel
          key={o.id}
          control={
            <Checkbox
              size="small"
              checked={!!(o as unknown as Record<string, unknown>).isDefault}
              onChange={() => handleToggle(o.id)}
              disabled={disabled}
            />
          }
          label={o.label}
        />
      ))}
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const DEFAULT_LOCALES = ['en-US'];

export const V2VariableEditDialog: React.FC<V2VariableEditDialogProps> = ({
  open,
  variable,
  onSave,
  onClose,
  disabled = false,
  locales = DEFAULT_LOCALES,
}) => {
  const [edited, setEdited] = useState<FunnelVariable>(() => cloneVariable(variable));
  const [saving, setSaving] = useState(false);

  // Reset local state when variable changes (dialog opens with new variable)
  React.useEffect(() => {
    if (open) {
      setEdited(cloneVariable(variable));
    }
  }, [open, variable]);

  // ── Field handlers ──────────────────────────────────────────────

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEdited(prev => ({ ...prev, name: e.target.value }));
  }, []);

  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEdited(prev => ({ ...prev, description: e.target.value }));
  }, []);

  const handleDefaultValueChange = useCallback((dv: unknown) => {
    setEdited(prev => {
      const next = { ...prev };
      if (dv === undefined) {
        delete (next as Record<string, unknown>).defaultValue;
      } else {
        (next as Record<string, unknown>).defaultValue = dv;
      }
      return next;
    });
  }, []);

  const handleOptionsChange = useCallback((options: VariableOption[]) => {
    setEdited(prev => ({ ...prev, options }));
  }, []);

  // ── Save ────────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!edited.name.trim()) return;
    setSaving(true);
    try {
      await onSave(edited);
    } catch (error) {
      console.error('[V2VariableEditDialog] Save failed:', error);
    } finally {
      setSaving(false);
    }
  }, [edited, onSave]);

  // ── Default value section ───────────────────────────────────────

  const defaultValueEditor = useMemo(() => {
    const dv = (edited as unknown as Record<string, unknown>).defaultValue;

    switch (edited.type) {
      case 'string':
        return (
          <StringDefaultEditor
            defaultValue={dv as Record<string, string> | undefined}
            onChange={handleDefaultValueChange}
            locales={locales}
            disabled={disabled || saving}
          />
        );
      case 'number':
        return (
          <NumberDefaultEditor
            defaultValue={dv as number | undefined}
            onChange={handleDefaultValueChange}
            min={edited.min}
            max={edited.max}
            disabled={disabled || saving}
          />
        );
      case 'boolean':
        return (
          <BooleanDefaultEditor
            defaultValue={dv as boolean | undefined}
            onChange={handleDefaultValueChange}
            disabled={disabled || saving}
          />
        );
      case 'date':
        return (
          <DateDefaultEditor
            defaultValue={dv as string | undefined}
            onChange={handleDefaultValueChange}
            disabled={disabled || saving}
          />
        );
      case 'enum':
        return (
          <EnumDefaultEditor
            options={edited.options ?? []}
            onChange={handleOptionsChange}
            disabled={disabled || saving}
          />
        );
      case 'enum[]':
        return (
          <EnumMultiDefaultEditor
            options={edited.options ?? []}
            onChange={handleOptionsChange}
            disabled={disabled || saving}
          />
        );
      default:
        return null;
    }
  }, [edited, disabled, saving, handleDefaultValueChange, handleOptionsChange]);

  const hasChanges = useMemo(
    () => JSON.stringify(edited) !== JSON.stringify(variable),
    [edited, variable],
  );

  return (
    <SDialog
      data-testid={V2_VARIABLES.editDialog}
      open={open}
      onClose={onClose}
      title={`Edit Variable: ${variable.name}`}
      maxWidth="sm"
      fullWidth
      primaryAction={{
        label: saving ? 'Saving...' : 'Save Changes',
        onClick: handleSave,
        disabled: disabled
          || saving
          || !edited.name.trim()
          || typeof edited.excludeFromAnalytics !== 'boolean'
          || !hasChanges,
        variant: 'contained',
        'data-testid': V2_VARIABLES.editSaveButton,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
        {/* Name */}
        <TextField
          data-testid={V2_VARIABLES.editNameInput}
          label="Name"
          value={edited.name}
          onChange={handleNameChange}
          size="small"
          fullWidth
          disabled={disabled || saving}
        />

        {/* Description */}
        <TextField
          data-testid={V2_VARIABLES.editDescriptionInput}
          label="Description"
          value={edited.description ?? ''}
          onChange={handleDescriptionChange}
          size="small"
          fullWidth
          disabled={disabled || saving}
          placeholder="For AI context and documentation"
        />

        <AnalyticsPrivacyDecisionField
          value={edited.excludeFromAnalytics}
          onChange={(value) => setEdited((previous) => ({
            ...previous,
            excludeFromAnalytics: value,
          }))}
          testIdPrefix={V2_VARIABLES.editDecisionPrefix}
          disabled={disabled || saving}
        />

        <Divider />

        {/* Default Value Section */}
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            Default Value (content fallback)
          </Typography>

          <Alert severity="info" sx={{ mb: 1.5, py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
            <Typography variant="caption">
              Used only for @variable text substitution. Does not affect conditions.
            </Typography>
          </Alert>

          {(edited.type === 'enum' || edited.type === 'enum[]') && (!edited.options || edited.options.length === 0) ? (
            <Typography variant="body2" color="text.secondary">
              No options defined. Add options via screen binding to set defaults.
            </Typography>
          ) : (
            defaultValueEditor
          )}
        </Box>

        {saving && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Box>
    </SDialog>
  );
};
